import { IModel, Collection, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';

import { SplitConditionExpression } from 'app/model/dataflow/split-condition-expression';
import { DataFlowStream } from 'app/model/dataflow/transformation';
import { sanitizeAndFormatString, stringIsNullOrEmpty } from '../../shared/utils/string.util';
import { DataFlowExpression } from './dataflowexpression';
import { Owned } from '../base/named';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';
import { Constants } from 'app/shared/shared-constants';
import { DataFlowTypesUtils } from './dataflow-type-utilities';
import { BaseRowModifierTransformation } from './base-row-modifier-transformation';
import { IExtraFieldInfo } from 'app/interfaces/IExtraFieldInfo';
import { BooleanDataFlowType } from './dataflow-datatype';

@Metamodel.model({
    name: 'split',
    display: [stringResources.DataFlowConditionalSplitTransformationLabel, stringResources.DataFlowConditionalSplitTransformationsLabel],
    referable: true,
    constraints: {
        name: {
            required: true
        }
    },
    validator: ConditionalSplitTransformation.validateConditionalSplitTransformation
})
export class ConditionalSplitTransformation extends BaseRowModifierTransformation {
    public static readonly MODEL: IModel;
    public transformationLabel = stringResources.DataFlowConditionalSplitTransformationLabel;

    public inspectSummaryOutputFields: IExtraFieldInfo[] = [
        {
            name: () => stringResources.TransformationInspect_NumberOfSplit,
            value: () => this.outputStreamCount
        },
        {
            name: () => stringResources.TransformationInspect_OutputStreams,
            value: () => this.outputStreamNames
        }
    ];

    @Metamodel.attribute({
        transient: true
    })
    public get visibleOutputStreams(): DataFlowStream[] {
        const orderedOutputStreams = Array.from(this.outputStreams);
        const defaultExpression = this.conditionExpressions.find((expression) => expression.isDefault);
        if (defaultExpression) {
            orderedOutputStreams.splice(orderedOutputStreams.indexOf(defaultExpression.outputStream), 1);
            orderedOutputStreams.push(defaultExpression.outputStream);
            return orderedOutputStreams;
        } else {
            return orderedOutputStreams;
        }
    }

    @Metamodel.attribute({
        display: stringResources.DataFlowConditionalSplitsLabel,
        collection: Collection.Array,
        constraint: {
            minOccurs: 1
        }
    })
    public conditionExpressions: SplitConditionExpression[] = [];

    /**
     * Property to determine if split is disjoint (i.e. same rows can go in different streams)
     */
    public disjoint?: boolean = false;

    public hasInputColumnNameErrors: boolean;

    @Metamodel.attribute({
        transient: true
    })
    private _currentColumns: Map<DataFlowStream, DataFlowColumn[]> = new Map<DataFlowStream, DataFlowColumn[]>();

    public get columns(): DataFlowColumn[] {
        const columns: DataFlowColumn[] = [];
        this.outputStreams.forEach((stream: DataFlowStream) => columns.push(...this._currentColumns.get(stream)));
        return columns;
    }

    protected get descriptionMsg(): string {
        return stringResources.SplitDescription;
    }

    protected get noDescriptionMsg(): string {
        return stringResources.SplitNoConditionDescription;
    }

    private get outputStreamCount(): number {
        return this.outputStreams.size;
    }

    private get outputStreamNames(): string {
        const names: string[] = [];
        this.outputStreams.forEach((stream) => names.push(stream.name));
        return names.join(', ');
    }

    public static validateConditionalSplitTransformation(transformation: ConditionalSplitTransformation): IViolation[] | undefined {
        transformation.validateInputColumns();
        return transformation.validateExpressions() || [];
    }

    public validateInputColumns(): void {
        const duplicateColumnNames = new Set<string>();
        this.distinctInputColumnRefs.forEach((columnRef) => {
            if (columnRef.isAmbiguous) {
                duplicateColumnNames.add(columnRef.column.name);
            }
        });
        this.hasInputColumnNameErrors = duplicateColumnNames.size > 0;
        return;
    }

    public validateExpressions(): IViolation[] | undefined {
        const violations: IViolation[] = [];
        this.conditionExpressions.forEach((conditionExpression, index) => {
            const streamHasName = conditionExpression.outputStream && !stringIsNullOrEmpty(conditionExpression.outputStream.name);
            const streamNameDuplicate = this.conditionExpressions.some((duplicateExpression, otherIndex) =>
                duplicateExpression.outputStream.name === conditionExpression.outputStream.name && index !== otherIndex);
            const streamInvalidName = streamHasName && !Constants.alphaNumericWithAlphaInitialValidation.test(conditionExpression.outputStream.name);
            const missingExpression = !conditionExpression.isDefault && (!conditionExpression.dataFlowExpression || stringIsNullOrEmpty(conditionExpression.dataFlowExpression.expression));
            const wrongReturnType = conditionExpression.dataFlowExpression && !stringIsNullOrEmpty(conditionExpression.dataFlowExpression.expression) &&
                !conditionExpression.dataFlowExpression.returnsCorrectType;
            if (!streamHasName) {
                violations.push({
                    context: conditionExpression,
                    message: () => stringResources.ConditionalSplitMissingStreamName,
                    attributes: [ConditionalSplitTransformation.MODEL.attribute('conditionExpressions')]
                });
            } else if (streamNameDuplicate) {
                violations.push({
                    context: conditionExpression,
                    message: () => sanitizeAndFormatString(stringResources.ConditionalSplitDuplicateStreamName, conditionExpression.outputStream.name),
                    attributes: [ConditionalSplitTransformation.MODEL.attribute('conditionExpressions')]
                });
            }

            if (streamInvalidName) {
                violations.push({
                    context: conditionExpression,
                    message: () => sanitizeAndFormatString(stringResources.Validator_ErrorMessage_AlphaNumberic, conditionExpression.outputStream.name),
                    attributes: [ConditionalSplitTransformation.MODEL.attribute('conditionExpressions')]
                });
            }
            if (missingExpression) {
                violations.push({
                    context: conditionExpression,
                    message: () => !streamHasName ? stringResources.ConditionalSplitMissingExpressionNoName :
                        sanitizeAndFormatString(stringResources.ConditionalSplitMissingExpression, conditionExpression.outputStream.name),
                    attributes: [ConditionalSplitTransformation.MODEL.attribute('conditionExpressions')]
                });
            }
            if (wrongReturnType) {
                violations.push({
                    context: conditionExpression,
                    message: () => stringResources.DataFlow_ShouldReturnBoolean,
                    attributes: [ConditionalSplitTransformation.MODEL.attribute('conditionExpressions')]
                });
            }
        });
        return violations;
    }

    public updateOutputColumns(): void {
        this._currentColumns.clear();

        this.outputStreams.forEach((outputStream) => {
            this._currentColumns.set(outputStream, []);
            this.inputColumns.forEach((column) => {
                const dataflowColumn = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), this);
                dataflowColumn.name = column.name;
                dataflowColumn.dataFlowColumnType = DataFlowTypesUtils.cloneType(column.dataFlowColumnType);
                dataflowColumn.basedOnArguments.push(column);
                this._currentColumns.get(outputStream).push(dataflowColumn);
            });
        });
    }

    public addOutputStream(streamName?: string, isDefault?: boolean, existingTransformation?: boolean, index?: number): DataFlowStream {
        const stream = super.addOutputStream(streamName);
        const conditionExpression = Owned.create<SplitConditionExpression>(SplitConditionExpression.MODEL, this);
        conditionExpression.outputStream = stream;
        // Add default stream by default.
        conditionExpression.isDefault = !!isDefault;
        if (isDefault) {
            this.conditionExpressions.push(conditionExpression);
        } else {
            if (!existingTransformation) {
                // If it is a new transformation added to canvas, add default stream
                this.addOutputStream('', true, true);
            }
            conditionExpression.dataFlowExpression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, conditionExpression);
            conditionExpression.dataFlowExpression.expression = '';
            conditionExpression.dataFlowExpression.requiredReturnType = BooleanDataFlowType.TYPE;
            if (index != null && index >= 0) {
                this.conditionExpressions.splice(index, 0, conditionExpression);
            } else {
                const indexOfDefault = this.conditionExpressions.findIndex((expression) => expression.isDefault);
                if (indexOfDefault === -1) {
                    this.conditionExpressions.push(conditionExpression);
                } else {
                    this.conditionExpressions.splice(this.conditionExpressions.length - 1, 0, conditionExpression);
                }
            }
        }

        return stream;
    }

    public getExpressions(): DataFlowExpression[] {
        return this.conditionExpressions
            .filter((conditionExpression) => conditionExpression.dataFlowExpression)
            .map((conditionExpression) => conditionExpression.dataFlowExpression);
    }

    protected updateInputTransformationCacheInternal(): void {
        super.updateInputTransformationCacheInternal();
        this.updateOutputColumns();
    }

    protected _outputColumns(outputStream?: DataFlowStream): DataFlowColumn[] {
        outputStream = outputStream || (this.conditionExpressions.find((ce) => ce.isDefault) ? this.conditionExpressions.find((ce) => ce.isDefault).outputStream : this.getFirstVisibleOutputStream());

        if (this._currentColumns.has(outputStream)) {
            return this._currentColumns.get(outputStream);
        }

        this.updateOutputColumns();
        return this._currentColumns.get(outputStream);
    }

    protected getOutputColumnStream(column: DataFlowColumn): DataFlowStream {
        return this.outputStreams.find((stream) => this._outputColumns(stream).findIndex((col) => col === column) >= 0);
    }
}
